import { useTheme } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, CircularProgress, Dialog, DialogContent, Grid, IconButton, Stack, Switch, Typography, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Context } from '../../../App';
import { useGetShopConfig, useUpdateBufferConfig, useUpdateDropOffSlotsConfig } from '../../../services/core/settings/settings.hooks';
import { APPOINTMENT_REVIEW_PLATFORM } from '../../../utility/Constant';
import { copyToClipboard } from '../../../utility/helpers/commonFunction';
import { default as SuccessMessageModal } from '../../Common/SuccessMessage';
import EmailCustomer from '../PersonalInfo/EmailCustomer';
import DropOffSlots from './DropOffSlots';

const styles = {
    container: {
        boxShadow: 3,
        bgcolor: 'white',
        justifyContent: 'space-between',
        borderTop: 'none',
        padding: '24px',
        borderRadius: '10px',
        marginTop: '32px',
        rowGap: 3,
    },
    title: {
        fontWeight: 400,
        fontSize: '24px',
    },
    description: {
        color: '#000',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        marginTop: 1.5,
    },
    controlsContainer: {
        // borderBottom: '1px solid #0000003B',
        height: 'max-content',
        paddingBottom: '8px',
    },
    labelContainer: {
        fontWeight: 500,
        fontSize: '20px',
        lineHeight: '160%',
        letterSpacing: '0.15px',
        // color: '#000',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
    },
    infoIcon: {
        cursor: 'pointer',
        color: '#8C8C8C',
        width: '24px',
        height: '24px',
    },
    controlsRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
    },
    numberBox: {
        border: '1px solid #0000003B',
        borderRadius: '6px',
        width: '122px',
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0,
        padding: '0 12px!important',
    },
    numberText: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '24px',
        letterSpacing: '0.15px',
        color: '#000',
    },
    labelText: {
        marginLeft: '-10px',
        fontSize: '14px',
        fontWeight: 600,
        color: '#006CDD',
        textTransform: 'uppercase',
        letterSpacing: '-0.42px',
        textAlign: 'center',
        lineHeight: '24px',
    },
    iconButton: {
        color: '#006CDD',
    },
};

const OnlineBooking = () => {
    const theme = useTheme();
    const WEB_ENDPOINT = process.env.REACT_APP_ONLINE_WEB_URL;
    const navigate = useNavigate();
    const { isOpenSideBar } = useContext(Context);

    // TanStack Query hooks
    const { data: configData, isLoading: isConfigLoading, error: configError } = useGetShopConfig();

    const updateBufferMutation = useUpdateBufferConfig();
    const updateDropOffSlotsMutation = useUpdateDropOffSlotsConfig();

    // Local state
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isBufferActive, setIsBufferActive] = useState(false);
    const [registeredUrl, setRegisteredUrl] = useState('');
    const [featuredRegisteredUrl, setFeaturedRegisteredUrl] = useState('');
    const [storeBufferValues, setStoreBufferValues] = useState({
        bufferDays: 1,
        isBufferDaysEnabled: false,
    });
    const [bufferDays, setBufferDays] = useState(1);
    const [selectedButton, setSelectedButton] = useState(APPOINTMENT_REVIEW_PLATFORM.GOOGLE);
    const [appointmentReview, setAppointmentReview] = useState(false);
    const [invoiceReviewManualSelect, setInvoiceReviewManualSelect] = useState(false);
    const [shouldSendReviewSMSToCustomer, setShouldSendReviewSMSToCustomer] = useState(false);
    const [shouldSendReviewEmailToCustomer, setShouldSendReviewEmailToCustomer] = useState(false);
    const [shouldSendReviewNotificationToCustomer, setShouldSendReviewNotificationToCustomer] = useState(false);

    const [shopThankYouEmailCustomMessage, setShopThankYouEmailCustomMessage] = useState('');
    const [shopThankYouEmailClosingMessage, setShopThankYouEmailClosingMessage] = useState('');

    // Drop-off slots state
    const [isDropOffEnabled, setIsDropOffEnabled] = useState(false);
    const [maxAppointments, setMaxAppointments] = useState(1);
    const [dropOffFrequency, setDropOffFrequency] = useState(0); // 0 = 60 minutes, 1 = 30 minutes
    const [isAllAppointmentsEnabled, setIsAllAppointmentsEnabled] = useState(false);
    const [maxAllAppointments, setMaxAllAppointments] = useState(1);

    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Process config data when it's loaded
    useEffect(() => {
        if (configData) {
            const toBool = (val) => val === true || val === 'true';
            const toInt = (val) => parseInt(val, 10) || 0;

            if (configData._id) {
                setRegisteredUrl(configData.registeredUrl ?? '');
            }
            if (configData?.featuredRegisteredUrl) {
                setFeaturedRegisteredUrl(configData.featuredRegisteredUrl);
            }

            // Review settings
            const config = configData?.config || {};
            console.log('🚀 ~ OnlineBooking ~ config:', config);
            setSelectedButton(config.appointmentReviewPlatform || APPOINTMENT_REVIEW_PLATFORM.GOOGLE);
            setAppointmentReview(toBool(config.appointmentReviewManualSelect));
            setInvoiceReviewManualSelect(toBool(config.invoiceReviewManualSelect));
            setShouldSendReviewSMSToCustomer(toBool(config.shouldSendReviewSMSToCustomer));
            setShouldSendReviewEmailToCustomer(toBool(config.shouldSendReviewEmailToCustomer));
            setShouldSendReviewNotificationToCustomer(toBool(config.shouldSendReviewNotificationToCustomer));
            setShopThankYouEmailCustomMessage(configData?.shopThankYouEmailCustomMessage ?? '');
            setShopThankYouEmailClosingMessage(configData?.shopThankYouEmailClosingMessage ?? '');

            // Buffer settings
            const bDays = toInt(configData.bufferDays) || 1;
            const bActive = toBool(configData.isBufferDaysEnabled);
            setBufferDays(bDays);
            setIsBufferActive(bActive);
            setStoreBufferValues({
                bufferDays: bDays,
                isBufferDaysEnabled: bActive,
            });

            // Handle drop-off slots data
            const websiteConfig = config?.bookingSlotLimit?.website;
            if (websiteConfig) {
                setIsDropOffEnabled(toBool(websiteConfig.maxAppointmentPerSlotEnabled));
                setMaxAppointments(toInt(websiteConfig.maxAppointmentsPerSlot) || 1);
            } else {
                setIsDropOffEnabled(false);
                setMaxAppointments(1);
            }

            // Handle All Appointments data
            const allConfig = config?.bookingSlotLimit?.all;
            if (allConfig) {
                setIsAllAppointmentsEnabled(toBool(allConfig.maxAppointmentPerSlotEnabled));
                setMaxAllAppointments(toInt(allConfig.maxAppointmentsPerSlot) || 1);
            } else {
                setIsAllAppointmentsEnabled(false);
                setMaxAllAppointments(1);
            }

            // Handle drop-off frequency
            if (configData?.shopSlotTimeInMin !== undefined) {
                setDropOffFrequency(toInt(configData.shopSlotTimeInMin));
            } else {
                setDropOffFrequency(60);
            }
        }
    }, [configData]);

    // Handler for enabling/disabling the buffer
    const handleToggleBuffer = () => {
        setIsBufferActive((prev) => !prev);
    };

    // Handler for changing the buffer days
    const handleBufferChange = (action) => {
        if (action === 'increment') {
            setBufferDays((prev) => prev + 1);
        } else if (action === 'decrement' && bufferDays > 1) {
            setBufferDays((prev) => prev - 1);
        }
    };

    const saveChanges = async () => {
        const payload = {
            isBufferDaysEnabled: isBufferActive,
            bufferDays,
        };

        try {
            await updateBufferMutation.mutateAsync(payload);
            setSuccessMessage('Changes Saved');
            setOpenSuccessModal(true);
        } catch (error) {
            console.error('Failed to save buffer config:', error);
            // Handle error appropriately
        }
    };

    // Helper function to build complete payload
    const buildDropOffSlotsPayload = (overrides = {}) => {
        // Convert dropOffFrequency to shopSlotTimeInMin: 0 = 60, 1 = 30
        const frequency = overrides.dropOffFrequency !== undefined ? overrides.dropOffFrequency : dropOffFrequency;
        const shopSlotTimeInMin = frequency;

        return {
            shopSlotTimeInMin,
            website: {
                maxAppointmentPerSlotEnabled: overrides.isDropOffEnabled !== undefined ? overrides.isDropOffEnabled : isDropOffEnabled,
                maxAppointmentsPerSlot: overrides.maxAppointments !== undefined ? overrides.maxAppointments : maxAppointments,
            },
            all: {
                maxAppointmentPerSlotEnabled: overrides.isAllAppointmentsEnabled !== undefined ? overrides.isAllAppointmentsEnabled : isAllAppointmentsEnabled,
                maxAppointmentsPerSlot: overrides.maxAllAppointments !== undefined ? overrides.maxAllAppointments : maxAllAppointments,
            },
        };
    };

    // Drop-off slots handlers - Updated to remove optimistic updates
    const handleDropOffSwitchToggle = async (checked) => {
        try {
            // If turning ON "Limit Website & App Bookings Only", turn OFF "All Appointments"
            const payload = buildDropOffSlotsPayload({
                isDropOffEnabled: checked,
                isAllAppointmentsEnabled: checked ? false : isAllAppointmentsEnabled,
            });
            await updateDropOffSlotsMutation.mutateAsync(payload);
            // Update both states immediately for instant UI feedback
            setIsDropOffEnabled(checked);
            if (checked) {
                setIsAllAppointmentsEnabled(false);
            }
        } catch (error) {
            console.error('Failed to update drop-off slots:', error);
            // No need to revert since we never changed it optimistically
        }
    };

    const handleDropOffIncrement = async () => {
        if (maxAppointments >= 100) return;

        const newMaxAppointments = maxAppointments + 1;

        try {
            const payload = buildDropOffSlotsPayload({ maxAppointments: newMaxAppointments });
            await updateDropOffSlotsMutation.mutateAsync(payload);
            // UI will update automatically after query invalidation
            setMaxAppointments(newMaxAppointments);
        } catch (error) {
            console.error('Failed to update max appointments:', error);
        }
    };

    const handleDropOffDecrement = async () => {
        if (maxAppointments <= 1) return;

        const newMaxAppointments = maxAppointments - 1;

        try {
            const payload = buildDropOffSlotsPayload({ maxAppointments: newMaxAppointments });
            await updateDropOffSlotsMutation.mutateAsync(payload);
            // UI will update automatically after query invalidation
            setMaxAppointments(newMaxAppointments);
        } catch (error) {
            console.error('Failed to update max appointments:', error);
        }
    };

    const handleDropOffFrequencyChange = async (newValue) => {
        if (newValue !== null) {
            try {
                const payload = buildDropOffSlotsPayload({ dropOffFrequency: newValue });
                await updateDropOffSlotsMutation.mutateAsync(payload);
                // UI will update automatically after query invalidation
                setDropOffFrequency(newValue);
            } catch (error) {
                console.error('Failed to update drop-off frequency:', error);
            }
        }
    };

    // All Appointments handlers
    const handleAllAppointmentsSwitchToggle = async (checked) => {
        try {
            // If turning ON "All Appointments", turn OFF "Limit Website & App Bookings Only"
            const payload = buildDropOffSlotsPayload({
                isAllAppointmentsEnabled: checked,
                isDropOffEnabled: checked ? false : isDropOffEnabled,
            });
            await updateDropOffSlotsMutation.mutateAsync(payload);
            // Update both states immediately for instant UI feedback
            setIsAllAppointmentsEnabled(checked);
            if (checked) {
                setIsDropOffEnabled(false);
            }
        } catch (error) {
            console.error('Failed to update all appointments:', error);
        }
    };

    // Handler for drop-off frequency toggle
    const handleAllAppointmentsIncrement = async () => {
        if (maxAllAppointments >= 100) return;

        const newMaxAllAppointments = maxAllAppointments + 1;

        try {
            const payload = buildDropOffSlotsPayload({ maxAllAppointments: newMaxAllAppointments });
            await updateDropOffSlotsMutation.mutateAsync(payload);
            // UI will update automatically after query invalidation
            setMaxAllAppointments(newMaxAllAppointments);
        } catch (error) {
            console.error('Failed to update max all appointments:', error);
        }
    };

    const handleAllAppointmentsDecrement = async () => {
        if (maxAllAppointments <= 1) return;

        const newMaxAllAppointments = maxAllAppointments - 1;

        try {
            const payload = buildDropOffSlotsPayload({ maxAllAppointments: newMaxAllAppointments });
            await updateDropOffSlotsMutation.mutateAsync(payload);
            // UI will update automatically after query invalidation
            setMaxAllAppointments(newMaxAllAppointments);
        } catch (error) {
            console.error('Failed to update max all appointments:', error);
        }
    };

    //Custom Booking compoenent
    const OnlineBookingLinks = ({ title, url }) => {
        return (
            <Grid
                container
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ boxShadow: 3, bgcolor: 'white' }}
                justifyContent={'start'}
                alignItems={'center'}
                borderTop={'none'}
                padding={'24px'}
                borderRadius={'10px'}
                marginTop={'32px'}
                rowGap={2}
            >
                <Grid item lg={3.8} md={12} sm={12} xs={12} xl={5}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        {title}
                    </Typography>
                </Grid>
                <Grid item container xs={12} lg={8.2} xl={7}>
                    <Grid container item xs={12} sm={12} md={12} xl={12} justifyContent={{ xs: 'start', sm: 'end' }} columnGap={3} rowGap={1}>
                        <Grid item>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={() => {
                                    window.open(`${WEB_ENDPOINT}${url}`, '_blank');
                                }}
                            >
                                View ONLINE BOOKING LINK
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={() => {
                                    copyToClipboard(`${WEB_ENDPOINT}${url}`);
                                }}
                            >
                                COPY LINK TO SHARE
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <Box>
            {/* Online Booking Grid  */}
            <OnlineBookingLinks title={'Online Booking'} url={registeredUrl} />

            {/* Secondary Booking Grid  */}
            {/* {featuredRegisteredUrl && <OnlineBookingLinks title={'Detailing Booking'} url={featuredRegisteredUrl} />} */}

            <EmailCustomer
                selectedButton={selectedButton}
                setSelectedButton={setSelectedButton}
                appointmentReview={appointmentReview}
                setAppointmentReview={setAppointmentReview}
                invoiceReviewManualSelect={invoiceReviewManualSelect}
                setInvoiceReviewManualSelect={setInvoiceReviewManualSelect}
                shouldSendReviewSMSToCustomer={shouldSendReviewSMSToCustomer}
                setShouldSendReviewSMSToCustomer={setShouldSendReviewSMSToCustomer}
                shouldSendReviewEmailToCustomer={shouldSendReviewEmailToCustomer}
                setShouldSendReviewEmailToCustomer={setShouldSendReviewEmailToCustomer}
                shouldSendReviewNotificationToCustomer={shouldSendReviewNotificationToCustomer}
                setShouldSendReviewNotificationToCustomer={setShouldSendReviewNotificationToCustomer}
                shopName={configData?.shopName || ''}
                openingMessage={shopThankYouEmailCustomMessage}
                setOpeningMessage={setShopThankYouEmailCustomMessage}
                closingMessage={shopThankYouEmailClosingMessage}
                setClosingMessage={setShopThankYouEmailClosingMessage}
            />

            {/* Drop Off Slots Grid */}
            <DropOffSlots
                isOpenSideBar={isOpenSideBar}
                isEnabled={isDropOffEnabled}
                maxAppointments={maxAppointments}
                onSwitchToggle={handleDropOffSwitchToggle}
                onIncrement={handleDropOffIncrement}
                onDecrement={handleDropOffDecrement}
                isUpdating={updateDropOffSlotsMutation.isPending}
                dropOffFrequency={dropOffFrequency}
                onFrequencyChange={handleDropOffFrequencyChange}
                isAllAppointmentsEnabled={isAllAppointmentsEnabled}
                maxAllAppointments={maxAllAppointments}
                onAllAppointmentsSwitchToggle={handleAllAppointmentsSwitchToggle}
                onAllAppointmentsIncrement={handleAllAppointmentsIncrement}
                onAllAppointmentsDecrement={handleAllAppointmentsDecrement}
                slotFrequencies={configData?.slotFrequencies || [30, 60]}
            />

            {/* Buffer Grid */}
            <Grid
                container
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ boxShadow: 3, bgcolor: 'white' }}
                justifyContent={'space-between'}
                borderTop={'none'}
                padding={'24px'}
                borderRadius={'10px'}
                marginTop={'32px'}
                rowGap={3}
            >
                <Grid item xs={12} md={isOpenSideBar ? 12 : 6.5} lg={4.5}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        Buffer
                    </Typography>
                    <Typography mt={1.5} sx={{ fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}>
                        Add a buffer to avoid customers booking last minute, and add at least 1 or more days between the booking date and the appointment date.
                    </Typography>
                    <Typography mt={3} sx={{ fontWeight: 400, fontSize: '16px', lineHeight: '24px' }}>
                        Example: Setting a buffer for 1 day will prevent customers from booking on a Sunday for an appointment on Monday.
                    </Typography>
                </Grid>

                <Grid item xs={12} md={isOpenSideBar ? 12 : 5.255} lg={7.255}>
                    <Grid container item alignItems="center" spacing={2}>
                        <Grid item xs={12}>
                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                                <Typography sx={styles.labelContainer} style={{ opacity: isBufferActive ? 1 : 0.4 }}>
                                    Select Days
                                </Typography>

                                <Stack alignItems="flex-end" gap={2.5}>
                                    <Stack direction="row" alignItems="center" gap="10px">
                                        <Box sx={styles.numberBox}>
                                            <IconButton
                                                onClick={() => handleBufferChange('decrement')}
                                                disabled={!isBufferActive}
                                                sx={{
                                                    padding: '0',
                                                    '& .MuiIconButton-root': {
                                                        padding: '0',
                                                    },
                                                }}
                                            >
                                                <RemoveIcon style={{ color: isBufferActive ? '#006CDD' : '' }} />
                                            </IconButton>
                                            <Typography
                                                sx={{
                                                    fontSize: '16px',
                                                    lineHeight: '24px',
                                                    letterSpacing: '0.15px',
                                                    color: isBufferActive ? '#000' : '#80808082',
                                                }}
                                            >
                                                {bufferDays}
                                            </Typography>
                                            <IconButton
                                                onClick={() => handleBufferChange('increment')}
                                                disabled={!isBufferActive}
                                                sx={{
                                                    padding: '0',
                                                    '& .MuiIconButton-root': {
                                                        padding: '0',
                                                    },
                                                }}
                                            >
                                                <AddIcon style={{ color: isBufferActive ? '#006CDD' : '' }} />
                                            </IconButton>
                                        </Box>

                                        <Switch color="primary" size="medium" checked={isBufferActive} onChange={(e) => setIsBufferActive(e.target.checked)} />
                                    </Stack>

                                    <Stack flexDirection={'row'} alignItems={'flex-end'} gap={1.5} mr={'22.5px'}>
                                        <Button
                                            disabled={!isBufferActive}
                                            onClick={() => setBufferDays(3)}
                                            sx={{
                                                color: isBufferActive ? '#000' : '#000000DE',
                                                background: isBufferActive ? '#EBEBEB' : '#EBEBEB',
                                                padding: '4px 8px!important',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                textTransform: 'lowercase',
                                            }}
                                        >
                                            3 days
                                        </Button>

                                        <Button
                                            disabled={!isBufferActive}
                                            onClick={() => setBufferDays(7)}
                                            sx={{
                                                color: isBufferActive ? '#000' : '#000000DE',
                                                background: isBufferActive ? '#EBEBEB' : '#EBEBEB',
                                                padding: '4px 8px!important',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                textTransform: 'lowercase',
                                            }}
                                        >
                                            7 days
                                        </Button>

                                        <Button
                                            disabled={!isBufferActive}
                                            onClick={() => setBufferDays(10)}
                                            sx={{
                                                color: isBufferActive ? '#000' : '#000000DE',
                                                background: isBufferActive ? '#EBEBEB' : '#EBEBEB',
                                                padding: '4px 8px!important',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                textTransform: 'lowercase',
                                            }}
                                        >
                                            10 days
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Save and Cancel Buttons */}
                <Grid item container justifyContent="flex-end" xs={12} md={12} rowGap={2}>
                    <Grid item container spacing={2} xs={12} sm={12} md={12} justifyContent={'flex-end'}>
                        <Grid item>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                    setBufferDays(storeBufferValues.bufferDays);
                                    setIsBufferActive(storeBufferValues.isBufferDaysEnabled);
                                }}
                            >
                                Cancel Changes
                            </Button>
                        </Grid>

                        <Grid item>
                            <Button variant="contained" color="primary" onClick={saveChanges}>
                                Save Changes
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Loader and Success Message */}
            <Dialog fullScreen={false} open={isConfigLoading || updateBufferMutation.isPending || updateDropOffSlotsMutation.isPending}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </Box>
    );
};

export default OnlineBooking;
